// NOTE ensure the namespace and any dependencies are set up appropriately, for both server and client
let RivalIQ;
if (typeof window === 'undefined') {

    RivalIQ = {
        enums: {}
    };

    module.exports = RivalIQ.enums;
} else {
    RivalIQ = window.RivalIQ;
    RivalIQ.module('enums', function () {});
}

(function (enums) {
    'use strict';

    function booleanOr(fromPlan, fromOverride) {
        return fromPlan || fromOverride;
    }

    function bitmaskOr(fromPlan, fromOverride) {
        return fromPlan | fromOverride;
    }

    function integerMax(fromPlan, fromOverride) {
        return Math.max(fromPlan, fromOverride);
    }

    function integerMin(fromPlan, fromOverride) {
        return Math.min(fromPlan, fromOverride);
    }

    function integerMaxOrZero(fromPlan, fromOverride) {
        if (fromOverride === 0) {
            return 0;
        }

        return Math.max(fromPlan, fromOverride);
    }

    function stringNotNull(fromPlan, fromOverride) {
        return (fromOverride === null) ? fromPlan : fromOverride;
    }

    function stringPrepare(element) {
        return element.val();
    }

    function booleanPrepare(element) {
        return element.is(':checked');
    }

    function integerPrepare(element) {
        return parseInt(element.val());
    }

    const dataTypes = {
        INTEGER: 1,
        BOOLEAN: 2,
        BITMASK: 3,
        SELECTOR: 4
    };

    const constants = {
        usePlanValue: 'usePlanValue',
        dateRangeUnlimited: 1023
    };

    enums.planOverrides = {

        dataTypes: dataTypes,
        constants: constants,

        // Definitions of the individual plan fields. These appear in the
        // "edit user" API in the order listed here. There are several
        // additional fields that are plan-related are not simple column
        // overrides:
        //
        //      enforce_company_plan_limit
        //      supported_sources
        //
        // These "special" fields appear first (hard-coded) in the
        // "edit user" page followed by the plan override columns listed
        // here:

        definitions: [
            {
                planColumnName: 'revenue_type',
                uiElementName: 'revenueType',
                uiLabel: 'Revenue Type',
                type: dataTypes.SELECTOR,
                selectors: [
                    {
                        display: '[use plan default]',
                        value: ''
                    },
                    {
                        display: 'direct',
                        value: 'direct'
                    },
                    {
                        display: 'indirect',
                        value: 'indirect'
                    },
                    {
                        display: 'legacy',
                        value: 'legacy'
                    }
                ],
                compute: stringNotNull,
                prepare: stringPrepare
            },

            {
                planColumnName: 'max_companies',
                uiElementName: 'maxCompanies',
                uiLabel: 'Max Companies',
                type: dataTypes.INTEGER,
                min: constants.usePlanValue,
                max: 100000,
                compute: integerMax,
                prepare: integerPrepare
            },

            {
                planColumnName: 'user_limit',
                uiElementName: 'userLimit',
                uiLabel: 'Max Users',
                type: dataTypes.INTEGER,
                min: constants.usePlanValue,
                max: 1000,
                compute: integerMax,
                prepare: integerPrepare
            },

            {
                planColumnName: 'max_landscapes',
                uiElementName: 'maxLandscapes',
                uiLabel: 'Max Landscapes',
                type: dataTypes.INTEGER,
                min: constants.usePlanValue,
                max: 10000,
                allowZero: true,
                compute: integerMaxOrZero,
                prepare: integerPrepare
            },

            {
                planColumnName: 'max_companies_per_landscape',
                uiElementName: 'maxCompaniesPerLandscape',
                uiLabel: 'Max Companies per Landscape',
                type: dataTypes.INTEGER,
                min: constants.usePlanValue,
                max: 3000, // MUST MATCH config.maxCompaniesPerLandscape in ./config/default.js!
                compute: integerMax,
                prepare: integerPrepare
            },
            {
                planColumnName: 'max_company_days',
                uiElementName: 'maxCompanyDays',
                uiLabel: 'Max Company Days of Data',
                type: dataTypes.INTEGER,
                min: constants.usePlanValue,
                max: 2190000, // MUST MATCH config.maxCompanyDaysOfDataToServe in ./config/default.js!
                compute: integerMax,
                prepare: integerPrepare
            },

            {
                planColumnName: 'recycle_companies',
                uiElementName: 'recycleCompanies',
                uiLabel: 'Max Company Swaps',
                type: dataTypes.INTEGER,
                min: constants.usePlanValue,
                max: 100000,
                compute: integerMax,
                prepare: integerPrepare
            },

            {
                planColumnName: 'history_limit_days',
                uiElementName: 'historyLimitDays',
                uiLabel: 'History Limit Days',
                type: dataTypes.INTEGER,
                min: constants.usePlanValue,
                max: 1825,
                compute: integerMax,
                prepare: integerPrepare
            },

            {
                planColumnName: 'max_fb_ad_accounts',
                uiElementName: 'maxFbAdAccounts',
                uiLabel: 'Max Facebook Ad Accounts',
                type: dataTypes.INTEGER,
                min: constants.usePlanValue,
                max: 10000,
                compute: integerMax,
                prepare: integerPrepare
            },

            {
                planColumnName: 'twitter_max_tokens',
                uiElementName: 'twitterMaxTokens',
                uiLabel: 'Max Twitter Tokens',
                type: dataTypes.INTEGER,
                min: constants.usePlanValue,
                max: 100,
                compute: integerMax,
                prepare: integerPrepare
            },

            {
                planColumnName: 'class_1_data_freshness_minutes',
                uiElementName: 'class1DataFreshnessMinutes',
                uiLabel: 'Social Data Freshness Minutes',
                type: dataTypes.INTEGER,
                min: 60,
                max: constants.usePlanValue,
                compute: integerMin,
                prepare: integerPrepare
            },

            // This is an odd beast. In the UI it appears as a checkbox, but
            // the underlying representation is an integer:
            //
            //      checkbox    value   meaning
            //      ~~~~~~~~    ~~~~~   ~~~~~~~
            //      cleared      null   delete override and revert to plan default
            //      checked      1023   Unlimited
            {
                planColumnName: 'supported_main_time_periods',
                uiElementName: 'supportedMainTimePeriods',
                uiLabel: 'Enable All Date Ranges',
                type: dataTypes.BOOLEAN,
                compute: integerMax,
                prepare: function (element) {
                    return element.is(':checked') ? constants.dateRangeUnlimited : null;
                },
                queryForUi: function (value) {
                    return value === constants.dateRangeUnlimited;
                }
            },

            {
                planColumnName: 'extended_social_content_history',
                uiElementName: 'extendedSocialContentHistory',
                uiLabel: 'Extended Social Content History',
                type: dataTypes.BOOLEAN,
                compute: booleanOr,
                prepare: booleanPrepare
            },

            {
                planColumnName: 'has_gds_connector',
                uiElementName: 'hasGdsConnector',
                uiLabel: 'Google Data Studio Connector',
                type: dataTypes.BOOLEAN,
                compute: booleanOr,
                prepare: booleanPrepare
            },

            {
                planColumnName: 'has_api_access',
                uiElementName: 'hasApiAccess',
                uiLabel: 'API Access',
                type: dataTypes.BOOLEAN,
                compute: booleanOr,
                prepare: booleanPrepare
            },

            {
                planColumnName: 'api_max_concurrency',
                uiElementName: 'apiMaxConcurrency',
                uiLabel: 'API Max Concurrency',
                type: dataTypes.INTEGER,
                min: constants.usePlanValue,
                max: 100,
                compute: integerMax,
                prepare: integerPrepare
            },

            {
                planColumnName: 'api_max_pending_operation_concurrency',
                uiElementName: 'apiMaxPendingOpConcurrency',
                uiLabel: 'API Max Pending Op Concurrency',
                type: dataTypes.INTEGER,
                min: constants.usePlanValue,
                max: 100,
                compute: integerMax,
                prepare: integerPrepare
            },

            {
                planColumnName: 'api_max_hour_usage',
                uiElementName: 'apiMaxHourUsage',
                uiLabel: 'API Max Hour Usage',
                type: dataTypes.INTEGER,
                min: constants.usePlanValue,
                max: 10000,
                compute: integerMax,
                prepare: integerPrepare
            },

            {
                planColumnName: 'max_listening_saved_searches',
                uiElementName: 'maxListeningSavedSearches',
                uiLabel: 'Listening Max Saved Searches',
                type: dataTypes.INTEGER,
                min: constants.usePlanValue,
                max: 50,
                compute: integerMax,
                prepare: integerPrepare
            },

            {
                planColumnName: 'has_full_instant_search',
                uiElementName: 'hasFullInstantSearch',
                uiLabel: 'Has Full Instant Search',
                type: dataTypes.BOOLEAN,
                compute: booleanOr,
                prepare: booleanPrepare
            },

            {
                planColumnName: 'has_dedicated_csm',
                uiElementName: 'hasDedicatedCSM',
                uiLabel: 'Has Dedicated CSM',
                type: dataTypes.BOOLEAN,
                compute: booleanOr,
                prepare: booleanPrepare
            },

            {
                planColumnName: 'has_insights_history',
                uiElementName: 'hasInsightsHistory',
                uiLabel: 'Facebook Insights and LinkedIn Analytics History',
                type: dataTypes.BOOLEAN,
                compute: booleanOr,
                prepare: booleanPrepare
            },

            {
                planColumnName: 'has_likely_promoted',
                uiElementName: 'hasLikelyPromoted',
                uiLabel: 'Facebook Boosted Post Detection',
                type: dataTypes.BOOLEAN,
                compute: booleanOr,
                prepare: booleanPrepare
            },

            {
                planColumnName: 'has_quid_compete_branding',
                uiElementName: 'hasQuidCompeteBranding',
                uiLabel: 'Has Quid Compete Branding',
                type: dataTypes.BOOLEAN,
                compute: booleanOr,
                prepare: booleanPrepare
            },

            {
                planColumnName: 'has_white_label',
                uiElementName: 'hasWhiteLabel',
                uiLabel: 'Custom Branding',
                type: dataTypes.BOOLEAN,
                compute: booleanOr,
                prepare: booleanPrepare
            },

            {
                planColumnName: 'you_tube_older_content_updates',
                uiElementName: 'YouTubeOlderContentUpdates',
                uiLabel: 'YouTube Older Content Updates',
                type: dataTypes.BOOLEAN,
                compute: booleanOr,
                prepare: booleanPrepare
            },

            {
                planColumnName: 'has_multi_handle_private_basic',
                uiElementName: 'hasMultiHandlePrivateBasic',
                uiLabel: 'Has multi-handle basic',
                type: dataTypes.BOOLEAN,
                compute: booleanOr,
                prepare: booleanPrepare
            },

            {
                planColumnName: 'has_multi_handle_private_advanced',
                uiElementName: 'hasMultiHandlePrivateAdvanced',
                uiLabel: 'Has multi-handle advanced',
                type: dataTypes.BOOLEAN,
                compute: booleanOr,
                prepare: booleanPrepare
            },

            {
                planColumnName: 'has_estimated_impressions',
                uiElementName: 'hasEstimatedImpressions',
                uiLabel: 'Has estimated impressions',
                type: dataTypes.BOOLEAN,
                compute: booleanOr,
                prepare: booleanPrepare
            },

            {
                planColumnName: 'supported_sources',
                compute: bitmaskOr,
                prepare: integerPrepare,
                hideFromUi: true // requires special handling
            }
        ]
    };

})(RivalIQ.enums);
